import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const GroupPaymentSuccess = () => {
    const { publicId, confirmationNumber } = useParams();
    const [paymentData, setPaymentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchPaymentConfirmation();
    }, [publicId, confirmationNumber]);

    const fetchPaymentConfirmation = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/group-bookings/public/${publicId}/payment-success/${confirmationNumber}`);
            setPaymentData(response.data);
        } catch (error) {
            console.error('Error fetching payment confirmation:', error);
            setError(error.response?.data?.message || 'Failed to load payment confirmation');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading payment confirmation...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-8">
                    <div className="text-6xl mb-4">❌</div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Confirmation Error</h1>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <a 
                        href="https://tpcrm.io" 
                        className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Go to Homepage
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
            <div className="max-w-4xl mx-auto px-4 py-12">
                {/* Success Header */}
                <div className="text-center mb-8">
                    <div className="text-6xl mb-4">✅</div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
                    <p className="text-xl text-gray-600">You've successfully joined the group trip</p>
                </div>

                {/* Confirmation Details */}
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Trip Information */}
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Trip Details</h2>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm text-gray-500">Trip</div>
                                    <div className="font-medium text-gray-900">{paymentData.trip.title}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">Destination</div>
                                    <div className="font-medium text-gray-900">{paymentData.trip.destination}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">Travel Dates</div>
                                    <div className="font-medium text-gray-900">
                                        {formatDate(paymentData.trip.start_date)} - {formatDate(paymentData.trip.end_date)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">Package</div>
                                    <div className="font-medium text-gray-900">{paymentData.participant.package}</div>
                                </div>
                            </div>
                        </div>

                        {/* Payment Information */}
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Information</h2>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm text-gray-500">Confirmation Number</div>
                                    <div className="font-mono text-lg font-bold text-green-600">
                                        {paymentData.confirmation_number}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">Amount Paid</div>
                                    <div className="font-medium text-gray-900">
                                        ${parseFloat(paymentData.participant.amount_paid).toFixed(2)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">Traveler</div>
                                    <div className="font-medium text-gray-900">
                                        {paymentData.participant.name}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">Email</div>
                                    <div className="font-medium text-gray-900">
                                        {paymentData.participant.email}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Agent Contact Information */}
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Travel Agent</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm text-gray-500">Agent</div>
                                    <div className="font-medium text-gray-900">{paymentData.agent.name}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">Agency</div>
                                    <div className="font-medium text-gray-900">{paymentData.agent.agency}</div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm text-gray-500">Email</div>
                                    <a 
                                        href={`mailto:${paymentData.agent.email}`}
                                        className="font-medium text-blue-600 hover:text-blue-700"
                                    >
                                        {paymentData.agent.email}
                                    </a>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">Phone</div>
                                    <a 
                                        href={`tel:${paymentData.agent.phone}`}
                                        className="font-medium text-blue-600 hover:text-blue-700"
                                    >
                                        {paymentData.agent.phone}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Next Steps */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
                    <h2 className="text-xl font-semibold text-blue-900 mb-4">What's Next?</h2>
                    <div className="space-y-3 text-blue-800">
                        <div className="flex items-start space-x-3">
                            <div className="text-blue-600 mt-1">📧</div>
                            <div>
                                <strong>Confirmation Email:</strong> You'll receive a detailed confirmation email shortly with your booking information and travel documents.
                            </div>
                        </div>
                        <div className="flex items-start space-x-3">
                            <div className="text-blue-600 mt-1">👤</div>
                            <div>
                                <strong>Agent Contact:</strong> Your travel agent will be in touch with additional details, travel requirements, and group coordination information.
                            </div>
                        </div>
                        <div className="flex items-start space-x-3">
                            <div className="text-blue-600 mt-1">📋</div>
                            <div>
                                <strong>Travel Preparation:</strong> Keep an eye out for important travel information, packing lists, and group meeting details as your trip approaches.
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="text-center space-y-4">
                    <div className="space-x-4">
                        <a 
                            href={`mailto:${paymentData.agent.email}?subject=Group Trip: ${paymentData.trip.title}`}
                            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Contact Your Agent
                        </a>
                        <button 
                            onClick={() => window.print()}
                            className="inline-block bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            Print Confirmation
                        </button>
                    </div>
                    <div>
                        <a 
                            href="https://tpcrm.io" 
                            className="text-gray-600 hover:text-gray-800 underline"
                        >
                            Return to Homepage
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GroupPaymentSuccess;
